import React from "react";
import { useRoles } from "@scenes/safeguard";
import { Id, Identifiable, Role, SecondaryCriteria } from "@planzone/dto";
import { Props } from "./types";
import { TextForm } from "./textForm";
import { NumberForm } from "./numberForm";
import { ListForm } from "./listForm";
import { DateForm } from "./dateForm";
import { DocumentForm } from "./documentForm";
import { LinkForm } from "./linkForm";
import { ResourceForm } from "./resourceForm";
import { RichTextForm } from "./richTextForm";

function hasRole(role: Identifiable<Role>, roles: Id[]) {
  return !!roles.find((id) => id === role.uuid || id === role.name);
}

function isReadable(
  roles: Identifiable<Role>[],
  criteria: Identifiable<SecondaryCriteria>,
) {
  return !!roles.find((role) => hasRole(role, criteria.accessRoles.read));
}

function isWriteable(
  roles: Identifiable<Role>[],
  criteria: Identifiable<SecondaryCriteria>,
) {
  return !!roles.find((role) => hasRole(role, criteria.accessRoles.write));
}

function SecondaryCriteriaForm({
  criteria,
  value,
  projectTimezone,
  disabled,
  flexible,
  submit,
}: Props) {
  const roles = useRoles({});
  const [formData, setFormData] = React.useState(value);
  const readable = isReadable(roles, criteria);
  const writeable = isWriteable(roles, criteria);

  React.useEffect(() => {
    setFormData(value);
  }, [value]);

  return !readable ? (
    <div />
  ) : criteria.type === "text" ? (
    <TextForm
      value={formData}
      disabled={disabled}
      flexible={flexible}
      edit={setFormData}
      submit={submit}
      readOnly={!writeable}
    />
  ) : criteria.type === "number" ? (
    <NumberForm
      value={formData}
      disabled={disabled}
      edit={setFormData}
      submit={submit}
      readOnly={!writeable}
    />
  ) : criteria.type === "date" ? (
    <DateForm
      value={formData}
      projectTimezone={projectTimezone}
      disabled={disabled}
      edit={setFormData}
      submit={submit}
      readOnly={!writeable}
      required={criteria.attr.required === "true"}
    />
  ) : criteria.type === "list" ? (
    <ListForm
      value={formData}
      options={criteria.options}
      attr={criteria.attr}
      disabled={disabled}
      submit={submit}
      readOnly={!writeable}
    />
  ) : criteria.type === "document" ? (
    <DocumentForm
      value={formData}
      disabled={disabled}
      flexible={flexible}
      submit={submit}
      readOnly={!writeable}
    />
  ) : criteria.type === "link" ? (
    <LinkForm
      value={formData}
      disabled={disabled}
      edit={setFormData}
      submit={submit}
      readOnly={!writeable}
    />
  ) : criteria.type === "richText" ? (
    <RichTextForm
      value={formData}
      name={criteria.name}
      disabled={disabled}
      flexible={flexible}
      edit={setFormData}
      submit={submit}
      readOnly={!writeable}
    />
  ) : criteria.type === "resource" ? (
    <ResourceForm
      value={formData}
      disabled={disabled}
      submit={submit}
      readOnly={!writeable}
    />
  ) : (
    <TextForm
      value={formData}
      disabled={disabled}
      edit={setFormData}
      submit={submit}
      readOnly={!writeable}
    />
  );
}

export { SecondaryCriteriaForm };
